/* eslint-disable camelcase */
/* eslint-disable object-curly-newline */
import React, {
 useContext, useEffect, useMemo, useState
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card } from '@telus/koodo-ui-kit'
import { useHistory } from 'react-router-dom'
import { useLocation } from 'react-router'
import { APPLICATION_STATE, ApplicationContext, setLandingLogicLoader } from '@apps/contexts/ApplicationContext'
import { recordUserInfo } from '@apps/redux/features/CommonSlice'
import {
    setVerifyEmail,
    setVerifySessionId,
    resetAuth,
    VERIFY_MODE,
    VERIFY_STEP
} from '@apps/redux/features/AuthSlice'
import { getById, getBySlug, getShortTextFromPage } from '@apps/utils/contentful'
import { isSafeInternalPath } from '@apps/utils/navigation'
import {
    EMAIL_REGEX, EMAIL_DISALLOWED_KEYS, ANALYTICS_EVENT_TYPES, EMAIL_ENTRY_OPTIONS
} from '@apps/utils/constants'
import {
    GUEST_EMAIL_CONTENTFUL_SLUGS,
    generateGuestEmail,
    getGuestEmailFallbackCopy,
    isGuestEmailEnabled
} from '@apps/utils/guestEmail'
import { setPageAnalytics } from '@apps/utils/Analytics'
import Brands from '@brands/index'
import ConsumerActivationPaths from '@sites/consumer/activation/ConsumerActivationPaths'
import DealerActivationPaths from '@sites/dealer/activation/DealerActivationPaths'
import Sites from '@sites/index'
import useLocale from '@engine/hooks/useLocale'
import useRecaptcha from '@apps/components/ReCaptcha/index'
import useSite from '@engine/hooks/useSite'
import envConfig from '@platformConfig/index'
import EmailStep from './verify/EmailStep'
import OtpStep from './verify/OtpStep'
import SuccessStep from './verify/SuccessStep'
import AlreadyActivatedModal from './verify/AlreadyActivatedModal'
import useTagManagerInfo from '../../../_engine/hooks/useTagManagerInfo'

// Live email validation: how long to wait after the last keystroke before
// re-checking the address against EMAIL_REGEX (matches the legacy IntroPage).
const EMAIL_VALIDATION_DEBOUNCE_MS = 1000

// How long the "Generating.." state is held before the guest address appears.
// The address is minted locally and comes back instantly, so this pause exists
// for the rep's benefit — the design asks for a visible hand-off rather than a
// value materialising out of nowhere. It is also the seam to hang a server-side
// generator off later without touching the UI.
const GUEST_EMAIL_GENERATION_MS = 600

const VerifyPage = () => {
    const dispatch = useDispatch()
    const history = useHistory()

    const consumerActivationPaths = new ConsumerActivationPaths()
    const dealerActivationPaths = new DealerActivationPaths()

    const { lang } = useLocale()
    const site = useSite()

    // Pick the flow's paths explicitly, as PlansSection does.
    const activationPaths = site === Sites.dealer ? dealerActivationPaths : consumerActivationPaths

    const { verifyMode, email: reduxEmail, sessionId: reduxSessionId } = useSelector((state) => state.auth)
    const { content: { pageContent, featureFlags: pageFeatureFlags } } = useSelector((state) => state.common)

    const {
        [APPLICATION_STATE.CURRENT_LANGUAGE]: language,
        [APPLICATION_STATE.CURRENT_REGION]: region,
        [APPLICATION_STATE.SERVICES]: brandServices
    } = useContext(ApplicationContext)

    const {
        isVerifyToken,
        checkEmailEligibility,
        validateUserDetails,
        validateOtp,
        createCookie,
        retrieveCookie,
        deleteCookie
    } = brandServices

    const featureFlags = pageFeatureFlags?.[0] || {}
    const { isNewUser, isExistingUser } = featureFlags

    // ─── Contentful text ───────────────────────────────────────────────────────
    // Resolved this early because the guest-flow switch below is published here.
    const landingPageContent = useMemo(() => {
        if (!pageContent || !language) return {}
        return getById(pageContent, 'landingPage')?.[language] || {}
    }, [pageContent, language])

    // Guest flow is a dealer-only affordance and only makes sense when starting a
    // fresh activation — "continue your previous activation" needs the address
    // the customer actually registered with. The `enableDealerGuestEmail` short
    // text on landingPage acts as a kill switch: unpublished means on, "false"
    // turns it off.
    const isGuestEmailAvailable =
        site === Sites.dealer &&
        verifyMode === VERIFY_MODE.NEW &&
        isGuestEmailEnabled(getShortTextFromPage(landingPageContent, GUEST_EMAIL_CONTENTFUL_SLUGS.enabled))

    const {
        googleReCaptchaConfig: { siteKey, enableRecaptchaConsumer }
    } = envConfig

    const isRecaptchaEnabled = enableRecaptchaConsumer === true || enableRecaptchaConsumer === 'true'
    const { recaptchaRef, handleRecaptcha } = useRecaptcha()

    // ─── Step state ────────────────────────────────────────────────────────────
    const [step, setStep] = useState(VERIFY_STEP.EMAIL)
    const [email, setEmail] = useState(reduxEmail || '')
    const [emailEntryOption, setEmailEntryOption] = useState(EMAIL_ENTRY_OPTIONS.CUSTOMER)

    // True while the rep is activating with a throw-away address. Drives the
    // read-only field, the OTP bypass in handleEmailSubmit, and the guestFlow
    // analytics attribute. Declared here rather than beside its other uses
    // because useTagManagerInfo below needs it.
    const isGuestEmail = isGuestEmailAvailable && emailEntryOption === EMAIL_ENTRY_OPTIONS.AUTO_GENERATED
    const [isGeneratingEmail, setIsGeneratingEmail] = useState(false)
    const [otpCode, setOtpCode] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    // EmailStep error states
    const [emailError, setEmailError] = useState(false)
    const [serverError, setServerError] = useState(false)
    const [captchaError, setCaptchaError] = useState(false)

    // OtpStep error states
    const [showWrongCode, setShowWrongCode] = useState(false)
    const [showOtpExpired, setShowOtpExpired] = useState(false)
    const [showMaxAttempts, setShowMaxAttempts] = useState(false)
    const [showResendError, setShowResendError] = useState(false)
    const [showUserBlockedError, setShowUserBlockedError] = useState(false)
    const [isOtpDisabled, setIsOtpDisabled] = useState(false)
    const [isUserBlocked, setIsUserBlocked] = useState(false)
    const [resetTimerSignal, setResetTimerSignal] = useState(0)

    // Bumped on resend, when a new code is sent and the old digits must be wiped.
    // The kit keeps its own internal state and ignores an empty value prop, so
    // OtpStep uses this as a remount key to force a true reset. Failed attempts
    // intentionally keep the entered digits (the user fixes them in place).
    const [otpResetSignal, setOtpResetSignal] = useState(0)

    // Already-activated modal
    const [showAlreadyActivated, setShowAlreadyActivated] = useState(false)

    // Attempts counter — initialised from Contentful once text loads
    const [attemptsLeft, setAttemptsLeft] = useState(3)

    // cookieId lives in a ref so it survives across validateUser → verifyCode
    // without causing re-renders. The redux sessionId is the persisted copy.
    const sessionIdRef = React.useRef(reduxSessionId || '')

    // Wraps the EmailStep so we can refocus the email field after the
    // already-activated modal closes.
    const emailStepRef = React.useRef(null)

    // An already-activated account is discovered on the email step (the roles
    // lookup is what decides whether to send an OTP at all), but must not be
    // disclosed until the user has proven they own the address. Only the fact
    // that the address is activated waits here — the modal needs nothing else,
    // and the account payload is no longer fetched on this step at all. Showing
    // the modal on the email step let anyone learn, and read the full profile
    // of, any Koodo Prepaid account by typing its address.
    const pendingActivatedRef = React.useRef(false)

    const focusEmailInput = () => {
        setTimeout(() => emailStepRef.current?.querySelector('input')?.focus(), 0)
    }

    // Debounces the live email validation so we only re-check the address a beat
    // after the user stops typing. Cleared on unmount to avoid a late setState.
    const emailValidationTimeoutRef = React.useRef(null)
    useEffect(() => () => clearTimeout(emailValidationTimeoutRef.current), [])

    // Holds the pending guest-address generation so it can be cancelled when the
    // rep switches option again or leaves the page mid-generation.
    const guestEmailTimeoutRef = React.useRef(null)
    useEffect(() => () => clearTimeout(guestEmailTimeoutRef.current), [])

    // Reset auth state when the user navigates away so stale email/sessionId
    // cannot pre-fill or skip steps in a subsequent visit.
    useEffect(() => () => { dispatch(resetAuth()) }, [dispatch])

    const { shortTextFields, longTextFields } = landingPageContent

    const guestCopy = useMemo(() => getGuestEmailFallbackCopy(language), [language])

    const text = useMemo(() => {
        if (!shortTextFields) return {}
        return {
            registerHeader: getShortTextFromPage(landingPageContent, 'register-your-email-address-header'),
            continueHeader: getShortTextFromPage(landingPageContent, 'continue-your-activation'),
            registerInfo: getShortTextFromPage(landingPageContent, 'register-your-email-address-info'),
            registerInfoAdditional: getShortTextFromPage(landingPageContent, 'register-your-email-address-info-additional'),
            continueInfoAdditional: getShortTextFromPage(landingPageContent, 'continue-your-email-address-info-additional'),
            emailAddressLabel: getShortTextFromPage(landingPageContent, 'email-address'),
            emailPlaceholder: getShortTextFromPage(landingPageContent, 'email-address-placeholder'),
            registerLabel: getShortTextFromPage(landingPageContent, 'register'),
            continueLabel: getShortTextFromPage(landingPageContent, 'continue-activation-button-label'),
            goBack: getShortTextFromPage(landingPageContent, 'go-back'),
            invalidEmail: getBySlug(shortTextFields, 'invalidEmail')?.value,
            registerFailure: getBySlug(shortTextFields, 'registerFailure')?.value,
            captchaFailure: getBySlug(shortTextFields, 'captchaFailure')?.value,
            emailCharMaxLimit: Number(getBySlug(shortTextFields, 'email-char-max-limit')?.value) || 100,
            sentCode: getShortTextFromPage(landingPageContent, 'sent-the-security-code'),
            sentCodeBold: getShortTextFromPage(landingPageContent, 'sent-the-security-code-bold'),
            resend: getShortTextFromPage(landingPageContent, 'click-here-resend'),
            pleaseEnterCode: getShortTextFromPage(landingPageContent, 'please-enter-6-digit'),
            codeExpires: getShortTextFromPage(landingPageContent, 'code-expires'),
            codeExpiredMessage: getShortTextFromPage(landingPageContent, 'code-timer-expired-message'),
            verify: getShortTextFromPage(landingPageContent, 'verify'),
            userBlockedHeading: getShortTextFromPage(landingPageContent, 'user-blocked-heading'),
            useOtherEmail: getShortTextFromPage(landingPageContent, 'use-a-different-email-address'),
            selfServeEmailText: getShortTextFromPage(landingPageContent, 'self-serve-email-info'),
            wrongCode: getBySlug(shortTextFields, 'wrong-code')?.value,
            otpExpiredError: getBySlug(shortTextFields, 'otp-expired-error')?.value,
            otpMaxAttemptExceed: getBySlug(shortTextFields, 'otpMaxAttemptExceed')?.value,
            resendError: getBySlug(shortTextFields, 'resendError')?.value,
            userBlockAfterMultipleRetry: getBySlug(shortTextFields, 'userBlockAfterMultipleRetry')?.value,
            youHaveAttemptsLeftFirst: getBySlug(shortTextFields, 'you-have-attempts-left-first')?.value,
            youHaveAttemptsLeftSecond: getBySlug(shortTextFields, 'you-have-attempts-left-second')?.value,
            otpMaxAttempts: getBySlug(shortTextFields, 'otpMaxAttempts')?.value,
            otpMaxValidations: getBySlug(shortTextFields, 'otpMaxValidations')?.value,
            minsForUnblocking: getBySlug(shortTextFields, 'minsForUnblockingUser')?.value,
            otpExpirationTime: getBySlug(shortTextFields, 'otpExpirationTime')?.value,
            verificationSuccessfulFirst: getBySlug(shortTextFields, 'verification-successful-first')?.value,
            verificationSuccessfulSecond: getBySlug(shortTextFields, 'verification-successful-second')?.value,
            alreadyActivatedTitle: getBySlug(shortTextFields, 'already-activated-title')?.value,
            alreadyActivatedBody: getBySlug(shortTextFields, 'already-activated-body')?.value,
            goToPortal: getBySlug(shortTextFields, 'go-to-portal-label')?.value,
            goToPortalUrl: getBySlug(longTextFields, 'go-to-portal-url')?.value,
            activateOtherEmail: getBySlug(shortTextFields, 'activate-other-email-label')?.value,
            // Dealer guest flow. Falls back to in-code copy until the slugs are
            // published on the landingPage entry (see utils/guestEmail).
            generatingEmail: getShortTextFromPage(landingPageContent, 'generating-email-placeholder') || guestCopy.generating,
            dealerEmailHeader: getShortTextFromPage(landingPageContent, 'dealer-email-header') || guestCopy.header,
            dealerEmailInfo: getShortTextFromPage(landingPageContent, 'dealer-email-info') || guestCopy.info,
            dealerEmailInfoHighlight: getShortTextFromPage(landingPageContent, 'dealer-email-info-highlight') || guestCopy.infoHighlight,
            customerEmailOption: getShortTextFromPage(landingPageContent, 'use-customer-email-option') || guestCopy.customerOption,
            autoGeneratedEmailOption: getShortTextFromPage(landingPageContent, 'use-auto-generated-email-option') || guestCopy.autoGeneratedOption,
            // Blank until the slugs are published; generateGuestEmail then falls
            // back to its built-in prefix/domain, so the flow works either way.
            autoGeneratedEmailPrefix: getShortTextFromPage(landingPageContent, GUEST_EMAIL_CONTENTFUL_SLUGS.prefix),
            autoGeneratedEmailDomain: getShortTextFromPage(landingPageContent, GUEST_EMAIL_CONTENTFUL_SLUGS.domain)
        }
    }, [shortTextFields, longTextFields, landingPageContent, guestCopy])

    // ─── Analytics ───────────────────────────────────────────────────────────
    const location = useLocation()
    const fromPath = location.state?.from
    const returnPath = site === Sites.consumer && isSafeInternalPath(fromPath)
        ? fromPath
        : activationPaths.Base
    // VerifyPage's own events (the option click, OTP success) fire before
    // isGuestFlow reaches redux, so the live value is passed straight through.
    // Memoised because useTagManagerInfo keeps customFields in a useMemo dep list.
    const analyticsCustomFields = useMemo(() => ({ guestFlow: isGuestEmail }), [isGuestEmail])
    const tagManagerInfo = useTagManagerInfo({ customFields: analyticsCustomFields })

    const pushPageView = () => {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push(
            setPageAnalytics(location, 'form', region, language, tagManagerInfo)
        )
    }

    const pushAnalytics = ({
 click_text, event_type, event_click = ANALYTICS_EVENT_TYPES.CLICK, type = 'click', target_link, field_warning, error_response_code, guestFlow
} = {}) => {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push(
            setPageAnalytics(location, type, region, language, {
                ...tagManagerInfo,
                click_text,
                field_warning,
                error_response_code,
                // An event raised from the same tick as the state change it reports
                // cannot read that change off tagManagerInfo yet — this render still
                // holds the previous value. Such callers pass the new value here.
                ...(guestFlow === undefined ? {} : { guestFlow })
            }, event_type, event_click, target_link)
        )
    }

    useEffect(() => {
        setTimeout(() => pushPageView(), 0)
    }, [region, language])

    useEffect(() => {
        if (step === VERIFY_STEP.OTP) {
            pushAnalytics({ event_type: 'verify OTP step displayed' })
        }
    }, [step])

    useEffect(() => {
        if (showAlreadyActivated) {
            pushAnalytics({
                type: 'popup',
                event_click: ANALYTICS_EVENT_TYPES.POPUP,
                event_type: 'already activated modal displayed'
            })
        }
    }, [showAlreadyActivated])

    const syncSessionCookie = async () => {
        const { userSessionId, userEmail } = await retrieveCookie() || {}
        if (userSessionId && userEmail?.toLowerCase() === email?.toLowerCase()) {
            sessionIdRef.current = userSessionId
        } else {
            await deleteCookie()
            const { data } = await createCookie({ email, flag: false, sessionId: userSessionId })
            sessionIdRef.current = data?.sessionId || ''
        }
        dispatch(setVerifySessionId(sessionIdRef.current))
    }

    const clearOtpErrors = () => {
        setShowWrongCode(false)
        setShowOtpExpired(false)
        setShowMaxAttempts(false)
        setShowResendError(false)
        setShowUserBlockedError(false)
    }

    const callValidateUser = async () => {
        await syncSessionCookie()
        const { data, error } = await validateUserDetails({
            email,
            phoneNumber: '',
            provider: Brands.koodo,
            language: lang?.toUpperCase(),
            type: '',
            sessionId: sessionIdRef.current,
            maxAttempts: Number(text.otpMaxAttempts),
            maxValidations: Number(text.otpMaxValidations),
            minsForUnblockingUser: Number(text.minsForUnblocking),
            otpExpirationTime: Number(text.otpExpirationTime)
        })

        if (error || (data && !data.validate)) {
            setServerError(true)
            setIsLoading(false)
            return false
        }

        return true
    }

    const handleEmailChange = (value) => {
        setServerError(false)
        setCaptchaError(false)
        clearTimeout(emailValidationTimeoutRef.current)

        // Reject anything past the configured length outright (e.g. a long paste).
        if (value.length > text.emailCharMaxLimit) {
            setEmailError(true)
            return
        }

        const sanitizedEmail = value.replace(/[^a-zA-Z0-9@._+-]/g, '')
        setEmail(sanitizedEmail)

        // An empty field is not an error — clear immediately and skip the debounce.
        if (!sanitizedEmail) {
            setEmailError(false)
            return
        }

        // Debounced live validation: surface the invalid-email error a beat after
        // the user stops typing, and clear it once the address becomes valid.
        emailValidationTimeoutRef.current = setTimeout(() => {
            setEmailError(!sanitizedEmail.match(EMAIL_REGEX))
        }, EMAIL_VALIDATION_DEBOUNCE_MS)
    }

    const handleEmailBlur = () => {
        if (!email) {
            setEmailError(false)
            return
        }
        setEmailError(!email.match(EMAIL_REGEX))
    }

    const handleEmailKeyDown = (event) => {
        if (event.key === ' ' || EMAIL_DISALLOWED_KEYS.includes(event.key)) {
            event.preventDefault()
        }
    }

    const emailEntryOptions = useMemo(() => (isGuestEmailAvailable
        ? [
            { title: text.customerEmailOption, value: EMAIL_ENTRY_OPTIONS.CUSTOMER },
            { title: text.autoGeneratedEmailOption, value: EMAIL_ENTRY_OPTIONS.AUTO_GENERATED }
        ]
        : []), [isGuestEmailAvailable, text.customerEmailOption, text.autoGeneratedEmailOption])

    const handleEmailOptionChange = (option) => {
        if (option === emailEntryOption) return

        clearTimeout(emailValidationTimeoutRef.current)
        clearTimeout(guestEmailTimeoutRef.current)
        setEmailEntryOption(option)
        setEmailError(false)
        setServerError(false)
        setCaptchaError(false)

        // Both directions start from an empty field: switching back leaves no
        // dummy address to edit, and the guest option shows "Generating.." until
        // the new one lands.
        setEmail('')

        if (option === EMAIL_ENTRY_OPTIONS.AUTO_GENERATED) {
            setIsGeneratingEmail(true)
            // A fresh address every time, so a rep who toggles back and forth
            // never reuses the one from a previous activation.
            guestEmailTimeoutRef.current = setTimeout(() => {
                setEmail(generateGuestEmail({
                    prefix: text.autoGeneratedEmailPrefix,
                    domain: text.autoGeneratedEmailDomain
                }))
                setIsGeneratingEmail(false)
            }, GUEST_EMAIL_GENERATION_MS)
        } else {
            setIsGeneratingEmail(false)
        }

        pushAnalytics({
            click_text: option === EMAIL_ENTRY_OPTIONS.AUTO_GENERATED
                ? text.autoGeneratedEmailOption
                : text.customerEmailOption,
            event_type: 'email entry option click',
            // Derived from the option being selected, not from emailEntryOption:
            // setEmailEntryOption above has not re-rendered yet, so the state still
            // reads as the previously selected option.
            guestFlow: isGuestEmailAvailable && option === EMAIL_ENTRY_OPTIONS.AUTO_GENERATED
        })
    }

    const handleEmailSubmit = async () => {
        // The Continue button is inactive while loading, but Enter in the field
        // still reaches this handler — an in-flight address is not submittable.
        if (isGeneratingEmail) return

        if (!email || !email.match(EMAIL_REGEX)) {
            setEmailError(true)
            pushAnalytics({ event_type: 'verify email invalid', field_warning: text.invalidEmail })
            return
        }

        setIsLoading(true)
        setServerError(false)
        setCaptchaError(false)
        // Stale flag from a previous address would raise the modal for the wrong
        // account once this one's code is verified.
        pendingActivatedRef.current = false

        // reCAPTCHA
        if (isRecaptchaEnabled) {
            const captchaToken = await recaptchaRef.current.executeAsync()
            recaptchaRef.current.reset()
            const verified = await isVerifyToken(captchaToken)
            if (!verified?.success) {
                setCaptchaError(true)
                setIsLoading(false)
                pushAnalytics({ event_type: 'verify captcha failed', field_warning: text.captchaFailure })
                return
            }
        }

        pushAnalytics({ click_text: text.continueLabel, event_type: 'Register button click' })

        try {
            // Booleans only. The account and quote payloads this branch used to
            // read are PII (name, phone, ban, guids; the quote's port-in
            // authValue and billing address) and must not reach the browser
            // before the code is verified — the BFF evaluates the conditions
            // and sends back the decision. The post-login landing logic fetches
            // the real data once isLoggedIn flips.
            const { data: eligibility, error: eligibilityError } = await checkEmailEligibility(email)

            if (eligibilityError || !eligibility) {
                setServerError(true)
                setIsLoading(false)
                pushAnalytics({ event_type: 'verify email server error', field_warning: text.registerFailure, error_response_code: eligibilityError?.code })
                return
            }

            const { isAlreadyActivated, hasActivationAccess, hasInProgressQuote } = eligibility

            if (isAlreadyActivated) {
                // Already fully activated — a returning user, so they get an OTP
                // like any other. The modal is raised in handleVerify once the
                // code checks out; nothing about the account is revealed here.
                pendingActivatedRef.current = true
                const ok = await callValidateUser()
                if (ok) {
                    dispatch(setVerifyEmail(email))
                    setAttemptsLeft(Number(text.otpMaxAttempts) || 3)
                    clearOtpErrors()
                    setOtpCode('')
                    setStep(VERIFY_STEP.OTP)
                }
                setIsLoading(false)
                return
            } if (hasActivationAccess) {
                if (hasInProgressQuote) {
                    // Returning user with in-progress quote. The quote itself is
                    // left for the landing logic to load after the OTP passes.
                    // A guest address is exempt: there is no mailbox to receive the
                    // code, so routing it to OTP would strand the activation.
                    if (isExistingUser && !isGuestEmail) {
                        const ok = await callValidateUser()
                        if (ok) {
                            dispatch(setVerifyEmail(email))
                            setAttemptsLeft(Number(text.otpMaxAttempts) || 3)
                            clearOtpErrors()
                            setOtpCode('')
                            setStep(VERIFY_STEP.OTP)
                        }
                    } else {
                        await loginUser(email)
                    }
                } else if (isNewUser && !isGuestEmail) {
                    // New user
                    const ok = await callValidateUser()
                    if (ok) {
                        dispatch(setVerifyEmail(email))
                        setAttemptsLeft(Number(text.otpMaxAttempts) || 3)
                        clearOtpErrors()
                        setOtpCode('')
                        setStep(VERIFY_STEP.OTP)
                    }
                } else {
                    await loginUser(email)
                }
            } else {
                // Unauthorized — already activated with a different role
                setShowAlreadyActivated(true)
                setIsLoading(false)
                return
            }
        } catch (err) {
            setServerError(true)
            pushAnalytics({ event_type: 'verify email server error', field_warning: text.registerFailure })
        }

        setIsLoading(false)
    }

    const handleBack = () => {
        pushAnalytics({ click_text: 'Go back register modal popup link', event_type: 'Go back register modal popup click' })
        dispatch(resetAuth())
        history.push(returnPath)
    }

    const handleVerify = async (otp) => {
        const code = otp || otpCode
        if (!code || code.length < 6) return
        setIsLoading(true)

        const { data, error } = await validateOtp({
            email,
            phoneNumber: '',
            provider: Brands.koodo,
            otp: code
        })

        if (error) {
            setIsLoading(false)
            if (attemptsLeft === 0) {
                setShowMaxAttempts(true)
            } else {
                setShowOtpExpired(true)
            }
            setIsOtpDisabled(true)
            pushAnalytics({
                event_type: 'OTP verify error',
                field_warning: attemptsLeft === 0 ? text.otpMaxAttemptExceed : text.otpExpiredError,
                error_response_code: error?.code
            })
            return
        }

        if (data) {
            pushAnalytics({ click_text: text.verify, event_type: 'verify button click' })
            const { validate, maxAttempts, maxValidations } = data
            // Only an explicit validate:true is a pass. The previous guard also
            // required a counter on the response, and `undefined >= 0` is false,
            // so a validate:false carrying no maxAttempts/maxValidations fell
            // through to the success branches — logging the user in, and now
            // disclosing an activated account, on a failed code.
            if (validate !== true) {
                const remaining = maxAttempts ?? maxValidations
                // Keep the last known count when uCare reports none, rather than
                // blanking the counter OtpStep requires as a number.
                if (Number.isFinite(remaining)) setAttemptsLeft(remaining)
                if (remaining === 0) {
                    // No attempts left — block the input and show the max-attempts error
                    setShowMaxAttempts(true)
                    setIsOtpDisabled(true)
                    pushAnalytics({ event_type: 'OTP max attempts reached', field_warning: text.otpMaxAttemptExceed })
                } else {
                    setShowWrongCode(true)
                    pushAnalytics({ event_type: 'OTP wrong code', field_warning: text.wrongCode })
                }
            } else if (pendingActivatedRef.current) {
                // Verified, and the address belongs to an already-activated
                // account. Safe to disclose that now. No cookie and no
                // recordUserInfo: this user is not logging in to the activation
                // flow, they are being sent to Self-Serve — same as the previous
                // pre-OTP modal, minus the disclosure. The modal is driven
                // entirely by Contentful text, so no account data is needed.
                setShowAlreadyActivated(true)
                pushAnalytics({ event_type: 'OTP verification successful' })
            } else {
                // Verified
                const userInfo = {
                    email,
                    isLoggedIn: true,
                    selectedLanguage: language,
                    isGuestFlow: isGuestEmail
                }
                await syncSessionCookie()
                await createCookie({ email, flag: true, sessionId: sessionIdRef.current })

                // Dispatch last. This flips isLoggedIn, which re-runs the landing
                // logic, and that decides whether the user is logged in by reading
                // the cookie (getUserInfo -> retrieveCookie) after a fixed 1s wait.
                // Dispatching first started that timer while createCookie was still
                // in flight, so on a slow connection it found no cookie, treated the
                // user as logged out and returned without routing (MASC-17321).
                dispatch(recordUserInfo(userInfo))
                pushAnalytics({ event_type: 'OTP verification successful' })
                setStep(VERIFY_STEP.SUCCESS)
            }
        }

        setIsLoading(false)
    }

    const handleResend = async () => {
        pushAnalytics({ click_text: text.resend, event_type: 'resend otp click' })
        clearOtpErrors()
        await syncSessionCookie()

        const { data, error } = await validateUserDetails({
            email,
            phoneNumber: '',
            provider: Brands.koodo,
            language: lang?.toUpperCase(),
            type: '',
            sessionId: sessionIdRef.current,
            maxAttempts: Number(text.otpMaxAttempts),
            maxValidations: Number(text.otpMaxValidations),
            minsForUnblockingUser: Number(text.minsForUnblocking),
            otpExpirationTime: Number(text.otpExpirationTime)
        })

        if (error) {
            setShowUserBlockedError(true)
            setIsUserBlocked(true)
            pushAnalytics({ event_type: 'OTP user blocked', field_warning: text.userBlockAfterMultipleRetry, error_response_code: error?.code })
            return
        }

        if (data) {
            const { validate } = data
            if (!validate) {
                setShowResendError(true)
                pushAnalytics({ event_type: 'OTP resend error', field_warning: text.resendError })
                return
            }
            setOtpCode('')
            setOtpResetSignal((s) => s + 1)
            setIsOtpDisabled(false)
            setAttemptsLeft(Number(text.otpMaxAttempts) || 3)
            setResetTimerSignal((s) => s + 1)
        }
    }

    const handleOtpChange = (value) => {
        clearOtpErrors()
        setOtpCode(value)
    }

    const handleUseOtherEmail = () => {
        pushAnalytics({ click_text: text.useOtherEmail, event_type: 'use a different email' })
        setEmail('')
        clearTimeout(guestEmailTimeoutRef.current)
        setEmailEntryOption(EMAIL_ENTRY_OPTIONS.CUSTOMER)
        setIsGeneratingEmail(false)
        setOtpCode('')
        setEmailError(false)
        clearOtpErrors()
        setIsOtpDisabled(false)
        setIsUserBlocked(false)
        pendingActivatedRef.current = false
        setStep(VERIFY_STEP.EMAIL)
        focusEmailInput()
    }

    const loginUser = async (userEmail) => {
        const userInfo = {
            email: userEmail,
            isLoggedIn: true,
            selectedLanguage: language,
            // Always false in practice — the guest option is only offered on a new
            // activation, never on the continue/existing-user path — but passed
            // explicitly so the flag is never left over from a previous session.
            isGuestFlow: isGuestEmail
        }
        await createCookie({ email: userEmail, flag: true, sessionId: sessionIdRef.current })
        // Dispatch after the cookie exists, for the same reason as the OTP path.
        dispatch(recordUserInfo(userInfo))
        pushAnalytics({ event_type: 'existing user logged in' })
        setLandingLogicLoader(true)

        // Return to the landing route and let the landing logic route from there,
        // as IntroPage's loginUser does in production. Hardcoding account setup
        // sent users there without the quote the landing logic would have created.
        history.push(activationPaths.Base)
    }

    const handleVerifyComplete = () => {
        pushAnalytics({ event_type: 'verification complete' })

        // No navigation and no resetAuth here on purpose. The landing logic reruns
        // on isLoggedIn and routes on the quote's currentState — including calling
        // createQuote for a user who has none, which a hardcoded push to account
        // setup skipped. Navigating from here as well made the destination depend
        // on which finished first.
        //
        // resetAuth is left to the unmount cleanup: calling it while still mounted
        // clears verifyMode, and the !verifyMode guard then pushes Base, which can
        // bounce the user off a destination already chosen (MASC-17321).
    }

    const handleGoToPortal = () => {
        pushAnalytics({
            click_text: text.goToPortal,
            event_type: 'already activated - go to portal',
            target_link: text.goToPortalUrl
        })
        window.location.href = text.goToPortalUrl
    }

    const handleAlreadyActivatedUseOtherEmail = () => {
        pushAnalytics({ click_text: text.activateOtherEmail, event_type: 'already activated - activate other email' })
        setShowAlreadyActivated(false)
        setEmail('')
        clearTimeout(guestEmailTimeoutRef.current)
        setEmailEntryOption(EMAIL_ENTRY_OPTIONS.CUSTOMER)
        setIsGeneratingEmail(false)
        setServerError(false)
        // The modal is now raised from the OTP step, not the email step, so
        // dismissing it has to walk the user back rather than just clear the
        // field — otherwise they land on the OTP step with no email.
        setOtpCode('')
        clearOtpErrors()
        setIsOtpDisabled(false)
        setIsUserBlocked(false)
        pendingActivatedRef.current = false
        setStep(VERIFY_STEP.EMAIL)
        focusEmailInput()
    }

    const pageTitle = verifyMode === VERIFY_MODE.CONTINUE ? text.continueHeader : text.registerHeader
    const pageInfoSubText = verifyMode === VERIFY_MODE.CONTINUE ? text.continueInfoAdditional : text.registerInfoAdditional

    // Dealers get the guest-flow wording; the consumer page is untouched.
    const emailStepTitle = isGuestEmailAvailable ? text.dealerEmailHeader : pageTitle
    const emailStepInfoText = isGuestEmailAvailable ? text.dealerEmailInfo : text.registerInfo
    const emailStepInfoSubText = isGuestEmailAvailable ? '' : pageInfoSubText
    const emailStepInfoHighlight = isGuestEmailAvailable ? text.dealerEmailInfoHighlight : ''
    const submitLabel = text.continueLabel

    // No verifyMode means the user reached /verify without entering through the
    // Intro flow (browser back/forward or refresh). Send them back to
    // the landing page to start the flow — not to already-activated.
    if (!verifyMode) {
        history.push(activationPaths.Base)
        return null
    }

    return (
        <div className="max-w-6xl px-4 m-auto py-28 sm:py-32 lg:py-40" id='block-verify-user'>
            <div className="flex flex-col w-full max-w-4xl mx-auto">
                <Card
                    className="verify-card"
                    variant={{ borderRadius: 'small', style: 'lightest' }}
                    tokens={{
                        paddingTop: 32, paddingBottom: 32, paddingLeft: 32, paddingRight: 32
                    }}
                >
                    {step === VERIFY_STEP.EMAIL && (
                        <EmailStep
                            title={emailStepTitle}
                            infoText={emailStepInfoText}
                            infoSubText={emailStepInfoSubText}
                            infoHighlightText={emailStepInfoHighlight}
                            emailOptions={emailEntryOptions}
                            selectedEmailOption={emailEntryOption}
                            onEmailOptionChange={handleEmailOptionChange}
                            isEmailReadOnly={isGuestEmail}
                            isGeneratingEmail={isGeneratingEmail}
                            generatingLabel={text.generatingEmail}
                            emailLabel={text.emailAddressLabel}
                            emailPlaceholder={text.emailPlaceholder}
                            submitLabel={submitLabel}
                            backLabel={text.goBack}
                            email={email}
                            onEmailChange={handleEmailChange}
                            onEmailBlur={handleEmailBlur}
                            onEmailKeyDown={handleEmailKeyDown}
                            onSubmit={handleEmailSubmit}
                            onBack={handleBack}
                            isLoading={isLoading}
                            emailError={emailError ? text.invalidEmail : ''}
                            serverError={serverError}
                            serverErrorText={text.registerFailure}
                            captchaError={captchaError}
                            captchaErrorText={text.captchaFailure}
                            recaptchaRef={recaptchaRef}
                            handleRecaptcha={handleRecaptcha}
                            siteKey={isRecaptchaEnabled ? siteKey : ''}
                            emailCharMaxLimit={text.emailCharMaxLimit}
                            containerRef={emailStepRef}
                        />
                    )}

                    {step === VERIFY_STEP.OTP && (
                        <OtpStep
                            title={pageTitle}
                            email={email}
                            otpCode={otpCode}
                            onOtpChange={handleOtpChange}
                            onVerify={handleVerify}
                            onResend={handleResend}
                            onUseOtherEmail={handleUseOtherEmail}
                            isLoading={isLoading}
                            isDisabled={isOtpDisabled}
                            expirationMinutes={Number(text.otpExpirationTime) || 5}
                            resetTimerSignal={resetTimerSignal}
                            resetFieldSignal={otpResetSignal}
                            attemptsLeft={attemptsLeft}
                            youHaveAttemptsLeftFirst={text.youHaveAttemptsLeftFirst}
                            youHaveAttemptsLeftSecond={text.youHaveAttemptsLeftSecond}
                            sentCodeText={text.sentCode}
                            sentCodeBoldText={text.sentCodeBold}
                            resendLabel={text.resend}
                            pleaseEnterCodeText={text.pleaseEnterCode}
                            codeExpiresText={text.codeExpires}
                            codeExpiredText={text.codeExpiredMessage}
                            verifyLabel={text.verify}
                            userBlockedHeading={text.userBlockedHeading}
                            useOtherEmailLabel={text.useOtherEmail}
                            isUserBlocked={isUserBlocked}
                            showWrongCode={showWrongCode}
                            wrongCodeText={text.wrongCode}
                            showOtpExpired={showOtpExpired}
                            otpExpiredText={text.otpExpiredError}
                            showMaxAttempts={showMaxAttempts}
                            maxAttemptsText={text.otpMaxAttemptExceed}
                            showResendError={showResendError}
                            resendErrorText={text.resendError}
                            showUserBlockedError={showUserBlockedError}
                            userBlockedText={text.userBlockAfterMultipleRetry}
                            selfServeEmailText={text.selfServeEmailText}
                        />
                    )}

                    {step === VERIFY_STEP.SUCCESS && (
                        <SuccessStep
                            successFirstLine={text.verificationSuccessfulFirst}
                            successSecondLine={text.verificationSuccessfulSecond}
                            onComplete={handleVerifyComplete}
                            autoRedirectMs={1000}
                        />
                    )}

                </Card>
            </div>
            <div
                data-component="payment-summary-cookie-anchor"
                data-cookie-link-size="large"
                className="mt-8 flex justify-center"
            />
            <AlreadyActivatedModal
                isOpen={showAlreadyActivated}
                onGoToPortal={handleGoToPortal}
                onUseOtherEmail={handleAlreadyActivatedUseOtherEmail}
                title={text.alreadyActivatedTitle}
                bodyText={text.alreadyActivatedBody}
                goToPortalLabel={text.goToPortal}
                useOtherEmailLabel={text.activateOtherEmail}
            />
        </div>
    )
}

export default VerifyPage
